import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import type { Message, Decision } from '../types/decision';
import type { Participant } from '../types/participant';
import { Timestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ParticipantJoin } from './ParticipantJoin';
import { saveMessage, subscribeToMessages, getMessageHistory } from '../services/messages';
import { generateChatResponse, getResponseStatus } from '../services/functions';
import { ChatInterface } from './ChatInterface';
import { Button } from './ui/button';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Analytics from '../services/analytics';
import { useDecision } from '../App';



// Custom hook for tracking component renders
function useRenderCount(componentName: string, debugInfo?: Record<string, any>) {
  const renderCountRef = useRef(0);
  
  useEffect(() => {
    renderCountRef.current++;
    console.log(`[${componentName}] Render #${renderCountRef.current}`, debugInfo);
  });
  
  return renderCountRef.current;
}

// Type predicate function
function isDecision(obj: any): obj is Decision {
  return obj && typeof obj === 'object' &&
    'id' in obj && typeof obj.id === 'string' &&
    'question' in obj && typeof obj.question === 'string' &&
    'status' in obj && (obj.status === 'active' || obj.status === 'closed');
}

// Updated component signature with no props
export function DecisionPage() {
  const navigate = useNavigate();
  const subscriptionRef = useRef<(() => void) | null>(null);
  const { decisionId } = useParams<{ decisionId: string }>();
  const welcomeTimestampRef = useRef(Timestamp.now());

  // State declarations
  const [decision, setDecision] = useState<Decision | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [isAiResponding, setIsAiResponding] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [synthesisContent, setSynthesisContent] = useState<string | null>(null);
  const { setCurrentDecision } = useDecision();


  // Add tracking for debug only
  useRenderCount('DecisionPage', {
    decisionId,
    participantId: participant?.id,
    messagesCount: messages.length,
    hasSubscription: !!subscriptionRef.current
  });

  // Helper function to get first name
  const getFirstName = useCallback((fullName: string | undefined) => {
    if (!fullName) return 'the organizer';
    return fullName.split(' ')[0];
  }, []);

  // Welcome message function
  const welcomeMessage = useMemo((): Message => ({
    id: 'welcome',
    decisionId: decisionId!,
    participantId: 'system',
    content: `Hi! I'm an AI assistant helping ${getFirstName(decision?.creatorName)} gather input about the question above. Participants will chat with me privately, and then I'll summarize the group's viewpoint. Ready to share your thoughts?`,
    sender: 'model',
    timestamp: welcomeTimestampRef.current
  }), [decisionId, decision?.creatorName, getFirstName]);

  const loadDecision = useCallback(async () => {
    if (!decisionId) return;

    try {
      const decisionRef = doc(db, 'decisions', decisionId);
      const decisionSnap = await getDoc(decisionRef);

      if (!decisionSnap.exists()) {
        throw new Error('Decision not found');
      }

      const decisionData = {
        id: decisionSnap.id,
        ...decisionSnap.data()
      };

      if (isDecision(decisionData)) {
        setDecision(decisionData);
        setCurrentDecision(decisionData);
        Analytics.trackDecisionViewed(decisionId);
      } else {
        throw new Error('Invalid decision data');
      }

    } catch (err) {
      console.error('Error loading decision:', err);
      setError('Failed to load decision. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [decisionId, setCurrentDecision]);

 // Setup message subscription, but only once
const hasMountedRef = useRef(false);

/* eslint-disable react-hooks/exhaustive-deps */
useEffect(() => {
  // Skip if already mounted, missing data, or already have a subscription
  if (hasMountedRef.current || !decisionId || !participant || subscriptionRef.current) return;
  
  // Mark as mounted to prevent repeated subscription attempts
  hasMountedRef.current = true;
  
  console.log('[DecisionPage] Creating message subscription for the first time');
  
  // Use a local welcome message function that doesn't depend on external state
  const getWelcomeMessage = (): Message => ({
    id: 'welcome',
    decisionId: decisionId!,
    participantId: 'system',
    content: `Hi! I'm an AI assistant helping ${getFirstName(decision?.creatorName)} gather input about the question above. Participants will chat with me privately, and then I'll summarize the group's viewpoint. Ready to share your thoughts?`,
    sender: 'model' as const,  // Explicitly type as 'model'
    timestamp: welcomeTimestampRef.current
  });
  
  subscriptionRef.current = subscribeToMessages(
    decisionId,
    participant.id,
    (updatedMessages) => {
      setMessages(prevMessages => {
        if (updatedMessages.length === 0) {
          if (prevMessages.length === 1 && prevMessages[0].id === 'welcome') {
            return prevMessages;
          }
          return [getWelcomeMessage()];
        }
        return [getWelcomeMessage(), ...updatedMessages];
      });
    },
    (error) => {
      console.error('Subscription error:', error);
      setError('Failed to load messages. Please refresh the page.');
    }
  );
  
  return () => {
    if (subscriptionRef.current) {
      console.log('[DecisionPage] Cleaning up subscription');
      subscriptionRef.current();
      subscriptionRef.current = null;
    }
  };
}, [decisionId, participant]);
/* eslint-enable react-hooks/exhaustive-deps */

  // Load initial decision data
  useEffect(() => {
    loadDecision();
  }, [loadDecision]);

  useEffect(() => {
    if (!decision) return;
    setMessages([welcomeMessage]);
  }, [decision, welcomeMessage]);

  // Fetch the synthesis once when the chat loads
  useEffect(() => {
    const fetchSynthesis = async () => {
      if (!decisionId) return;
      
      try {
        const status = await getResponseStatus(decisionId);
        if (status.interimResponseId || status.finalResponseId) {
          const responseId = status.finalResponseId || status.interimResponseId;
          const responseRef = doc(db, 'decisions', decisionId, 'responses', responseId!);
          const responseSnap = await getDoc(responseRef);
          
          if (responseSnap.exists()) {
            const responseData = responseSnap.data();
            // Always get the content for chat context, even if we don't display it yet
            // in the response view due to participant count
            setSynthesisContent(responseData.content);
            
            console.log(`Loaded synthesis with ${responseData.participantCount} participants`);
          }
        }
      } catch (err) {
        console.log('No synthesis available yet:', err);
        // Not a critical error, continue without synthesis
      }
    };
    
    fetchSynthesis();
  }, [decisionId]);
  
  const handleSendMessage = async (messageContent: string) => {
    if (!participant || !decision) return;
  
    setIsSending(true);
    setError(null);
  
    try {
      // First save the message - this now handles status transition in its transaction
      await saveMessage({
        decisionId: decisionId!,
        participantId: participant.id,
        content: messageContent,
        sender: 'user'
      });
  
      setIsAiResponding(true);
      const messageHistory = await getMessageHistory(decisionId!, participant.id);
  
      // Generate AI response with the pre-fetched synthesis data
      await generateChatResponse({
        decisionId: decisionId!,
        participantId: participant.id,
        message: messageContent,
        decisionContext: {
          question: decision.question,
          details: decision.details,
          latestSynthesis: synthesisContent  // Include the synthesis content here
        },
        messageHistory
      });
  
    } catch (error) {
      // Error handling remains the same
      if (error instanceof Error) {
        if (error.message.includes('429')) {
          setError("We're processing too many requests right now. Please wait a moment and try again.");
        } else if (error.message.includes('quota')) {
          setError("We've hit our API limit. Please try again in a few minutes.");
        } else {
          setError('Failed to send message. Please try again.');
        }
      }
      console.error('Error in chat flow:', error);
    } finally {
      setIsSending(false);
      setIsAiResponding(false);
    }
  };

  const handleComplete = useCallback(async () => {
    if (!participant || !decisionId) return;
  
    // Check if the user has sent any messages before updating status
    const hasUserMessages = messages.some(msg => 
      msg.sender === 'user' && msg.participantId === participant.id
    );
  
    if (!hasUserMessages) {
      // No messages from this user, just navigate back WITHOUT justCompleted flag
      navigate(`/d/${decisionId}`);
      return;
    }
  
    setIsComplete(true);
    console.log('Starting completion flow...');
  
    try {
      console.log('Updating participant status...');
      const participantRef = doc(db, 'decisions', decisionId, 'participants', participant.id);
      await updateDoc(participantRef, {
        status: 'completed' as const,
        lastActive: Timestamp.now()
      });
      console.log('Status updated successfully');
  
      // Only pass justCompleted: true when we've actually updated status
      navigate(`/d/${decisionId}`, { 
        state: { justCompleted: true }
      });
  
    } catch (err) {
      console.error('Error in completion flow:', err);
      setError('Failed to update chat status. Please try again.');
      setIsComplete(false);
    }
  }, [participant, decisionId, navigate, messages]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRefreshSynthesis = async () => {
    try {
      const functions = getFunctions();
      const synthesize = httpsCallable(functions, 'synthesizeDecisionResponses');
      await synthesize({ decisionId });
      // Optionally add some UI feedback here
    } catch (err) {
      console.error('Error refreshing synthesis:', err);
      setError('Failed to refresh synthesis. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-3xl px-0">
        <Card>
          <CardHeader className="flex items-center justify-center min-h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-3xl px-0">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl px-0">
      <Card className="md:h-auto border-0 md:border">
        {decision && isDecision(decision) ? (
          decision.status === 'closed' ? (
            <div className="text-center p-6">
              <p className="text-muted-foreground">This discussion is now closed.</p>
              <Button onClick={() => navigate(`/d/${decisionId}/`)} className="mt-4">
                View Final Response
              </Button>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="flex-grow overflow-hidden">
                <CardContent className="p-0 flex flex-col h-full">
                  <ChatInterface
                    decisionId={decisionId!}
                    participant={participant}
                    messages={messages}
                    onSendMessage={handleSendMessage}
                    onComplete={handleComplete}
                    isAiResponding={isAiResponding}
                    isSending={isSending}
                    isComplete={isComplete}
                    error={error}
                    loading={loading}
                    readOnly={!participant} // Chat is read-only if no participant
                    decision={decision}
                  />
                  {!participant && (
                  <div className="p-4">
                    <ParticipantJoin
                      decisionId={decisionId!}
                      onJoined={(newParticipant: Participant) => setParticipant(newParticipant)}
                    />
                  </div>
                )}
                </CardContent>
              </div>
            </div>
          )
        ) : (
          <div className="text-center p-6">
            <p className="text-muted-foreground">Loading decision data...</p>
          </div>
        )}
      </Card>
    </div>
  );
}